import type { BrandKnowledge } from '../types';

const KNOWS_META: Record<BrandKnowledge['knows'], { label: string; color: string }> = {
  well: { label: 'knows you', color: '#00FFD4' },
  vaguely: { label: 'vaguely', color: '#E0B341' },
  no: { label: 'no', color: '#E0574A' },
};

function Pill({ label, value, color }: { label: string; value: string | number; color: string }): JSX.Element {
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="text-sm text-text-secondary">{label}</span>
      <span className="font-mono text-sm font-medium" style={{ color }}>
        {value}
      </span>
    </div>
  );
}

/**
 * Persistent live scoreboard: how often AI mentions / cites the brand across all
 * probes, and whether the model knows the brand from memory. Always visible once
 * any smer-B data exists, so the visitor keeps the real result in view.
 */
export default function Scoreboard({
  mentioned,
  cited,
  total,
  knows,
}: {
  mentioned: number;
  cited: number;
  total: number;
  knows: BrandKnowledge['knows'] | null;
}): JSX.Element | null {
  if (total === 0 && knows === null) return null;
  const km = knows ? KNOWS_META[knows] : null;
  return (
    <div className="w-full max-w-2xl mx-auto flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-warm-dark-2 bg-warm-dark-2/40 px-4 py-2.5">
      <span className="font-mono text-sm uppercase tracking-widest text-text-secondary">Live result</span>
      {total > 0 && (
        <>
          <Pill label="mentioned" value={`${mentioned}/${total}`} color="#00FFD4" />
          <Pill label="cited" value={`${cited}/${total}`} color="#E0B341" />
        </>
      )}
      {km && <Pill label="AI knows brand" value={km.label} color={km.color} />}
    </div>
  );
}
